import React from 'react';
import { Layout, Menu, Typography, Tooltip } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import type { MenuProps } from 'antd';

const { Sider } = Layout;
const { Text } = Typography;

export interface SiderMenuItem {
  key: string;
  label: string;
  icon: React.ReactNode;
}

export interface SiderMenuGroup {
  /** Gruppen-Label (optional; wenn leer, wird es nur als visuelle Trenn-Linie angezeigt) */
  title?: string;
  items: SiderMenuItem[];
}

export interface AppSiderProps {
  /** Hell (Customer) oder dunkel (Admin) */
  variant?: 'light' | 'dark';
  /** Akzentfarbe fuer Logo + Status-Dot */
  accent?: string;
  /** Zwei-Buchstaben Logo-Kuerzel (z.B. "SR", "KI") */
  logoInitials: string;
  /** Haupt-Title unter dem Logo */
  title: string;
  /** Rubrik / Untertitel (z.B. "ADMIN", "PORTAL") */
  subtitle?: string;
  /** Eingeklappt? */
  collapsed: boolean;
  /** Kollabier-Aenderung (z.B. durch Breakpoint) */
  onCollapseChange?: (v: boolean) => void;
  /** Gruppen von Menu-Eintraegen */
  groups: SiderMenuGroup[];
  /** Version/Build fuer den Footer */
  version?: string;
  /** System-Status-Label im Footer (z.B. "Alle Systeme online") */
  statusLabel?: string;
  /** Status-Variante: gruen (ok), gelb (degraded), rot (down) */
  statusVariant?: 'ok' | 'warn' | 'down';
}

const THEMES = {
  dark: {
    bg: 'linear-gradient(180deg, #0f172a 0%, #111827 100%)',
    brandBorder: 'rgba(255,255,255,0.08)',
    brandTitle: '#fff',
    brandSub: 'rgba(255,255,255,0.55)',
    footerBorder: 'rgba(255,255,255,0.08)',
    footerText: 'rgba(255,255,255,0.55)',
    footerTextStrong: 'rgba(255,255,255,0.85)',
    groupLabel: 'rgba(255,255,255,0.35)',
    shadow: '2px 0 12px rgba(15,23,42,0.15)',
  },
  light: {
    bg: '#ffffff',
    brandBorder: 'rgba(15,23,42,0.08)',
    brandTitle: 'rgba(15,23,42,0.92)',
    brandSub: 'rgba(15,23,42,0.55)',
    footerBorder: 'rgba(15,23,42,0.08)',
    footerText: 'rgba(15,23,42,0.55)',
    footerTextStrong: 'rgba(15,23,42,0.8)',
    groupLabel: 'rgba(15,23,42,0.4)',
    shadow: '1px 0 8px rgba(15,23,42,0.04)',
  },
} as const;

const STATUS_COLORS = {
  ok: '#22c55e',
  warn: '#f59e0b',
  down: '#ef4444',
} as const;

export function AppSider(props: AppSiderProps): React.ReactElement {
  const { pathname } = useLocation();
  const variant = props.variant ?? 'dark';
  const themeColors = THEMES[variant];
  const accent = props.accent ?? '#6366f1';
  const statusColor = STATUS_COLORS[props.statusVariant ?? 'ok'];
  const siderWidth = 240;
  const collapsedWidth = 72;

  const flatItems = props.groups.flatMap((g) => g.items);
  const selectedKeys = flatItems
    .filter((item) => pathname.startsWith(item.key))
    .map((item) => item.key);

  const menuItems: MenuProps['items'] = props.groups.flatMap((group, gi) => {
    const entries: NonNullable<MenuProps['items']> = [];
    if (group.title && !props.collapsed) {
      entries.push({
        key: `__g_${gi}`,
        type: 'group',
        label: (
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              color: themeColors.groupLabel,
              paddingLeft: 4,
            }}
          >
            {group.title}
          </span>
        ),
      });
    } else if (gi > 0 && props.collapsed) {
      entries.push({
        key: `__sep_${gi}`,
        type: 'divider',
        style: { background: themeColors.brandBorder, margin: '6px 12px' },
      });
    }
    for (const item of group.items) {
      entries.push({
        key: item.key,
        icon: item.icon,
        label: <Link to={item.key} style={{ color: 'inherit', textDecoration: 'none' }}>{item.label}</Link>,
      });
    }
    return entries;
  });

  return (
    <Sider
      theme={variant}
      width={siderWidth}
      collapsedWidth={collapsedWidth}
      collapsed={props.collapsed}
      onCollapse={props.onCollapseChange}
      trigger={null}
      breakpoint='lg'
      style={{
        background: themeColors.bg,
        boxShadow: themeColors.shadow,
        position: 'sticky',
        top: 0,
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
        }}
      >
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: props.collapsed ? 'center' : 'flex-start',
            padding: props.collapsed ? 0 : '0 20px',
            borderBottom: `1px solid ${themeColors.brandBorder}`,
            gap: 10,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: `linear-gradient(135deg, ${accent}, ${accent}b3)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              color: '#fff',
              fontSize: 14,
              boxShadow: `0 6px 16px ${accent}50`,
              flexShrink: 0,
            }}
          >
            {props.logoInitials}
          </div>
          {!props.collapsed && (
            <div style={{ minWidth: 0 }}>
              <Text
                strong
                style={{
                  color: themeColors.brandTitle,
                  fontSize: 14,
                  lineHeight: 1.15,
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {props.title}
              </Text>
              {props.subtitle && (
                <Text style={{ color: themeColors.brandSub, fontSize: 11, letterSpacing: 0.5 }}>
                  {props.subtitle}
                </Text>
              )}
            </div>
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingTop: 8 }}>
          <Menu
            theme={variant}
            mode='inline'
            selectedKeys={selectedKeys}
            items={menuItems}
            inlineCollapsed={props.collapsed}
            style={{ borderRight: 0, background: 'transparent' }}
          />
        </div>

        <div
          style={{
            borderTop: `1px solid ${themeColors.footerBorder}`,
            padding: props.collapsed ? '10px 0' : '12px 16px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: props.collapsed ? 'center' : 'space-between',
            gap: 10,
          }}
        >
          {props.collapsed ? (
            <Tooltip title={props.statusLabel ?? 'System online'} placement='right'>
              <span
                style={{
                  display: 'inline-block',
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: statusColor,
                  boxShadow: `0 0 0 4px ${statusColor}33`,
                }}
              />
            </Tooltip>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <span
                  style={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: statusColor,
                    boxShadow: `0 0 0 4px ${statusColor}33`,
                    flexShrink: 0,
                  }}
                />
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      color: themeColors.footerTextStrong,
                      fontSize: 12,
                      fontWeight: 500,
                      lineHeight: 1.1,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {props.statusLabel ?? 'Alle Systeme online'}
                  </div>
                  {props.version && (
                    <div style={{ color: themeColors.footerText, fontSize: 10 }}>{props.version}</div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Sider>
  );
}
